import { z } from 'zod';

/**
 * #420a — the PEOC↔COMMUNITY two-way COORDINATION thread (role-view council §6, the
 * §E sim G2-closer). An AMBIENT one-author / many-reader coordination log BOUND to a
 * community record (NOT the two-party privacy wall of agency_task_messages —
 * communities are held-global). Reachable from the community / flood detail
 * incident-free.
 *
 * The author is the posting operator's RESOLVED identity (the seat under AUTH-off),
 * stamped server-side; `author_label` is DERIVED at serialization (never stored) and
 * returns the operator's honest identity today — the two-audience PEOC/municipal label
 * is a 1-line serializer add when the role boot-seed ships. `mine` is computed
 * server-side (author_operator_id === the viewing operator) so the client needs ZERO
 * identity logic. No author_role and no per-message read receipt (both deferred).
 */
/**
 * A per-message READ RECEIPT (#453, migration 0139). One entry per acknowledging operator
 * (EXCLUDING the author — a message is never self-acknowledged). A receipt exists ONLY for a
 * real, EXPLICIT in-app acknowledge (never auto-on-render, never inferred from delivery).
 */
export const CommunityMessageReadReceiptSchema = z.object({
  /** The acknowledging operator's DERIVED seat label (never a raw id). */
  label: z.string(),
  /** ISO-8601 UTC acknowledge time. */
  at: z.string(),
});
export type CommunityMessageReadReceipt = z.infer<typeof CommunityMessageReadReceiptSchema>;

/**
 * The hazard families a coordination message can be BOUND to (migration 0154, §G rebuild Wave 2).
 * A message posted from the OC-fire panel binds to the fire so the community gets a HAZARD-LINKED
 * record (the First Nation seat's ruling: "I cannot get provenance off a phone call"). Only
 * 'active_wildland_fire' today; a new family is ONE literal here + a case in the display helper.
 */
export const HAZARD_SOURCE_TYPES = ['active_wildland_fire'] as const;
export const HazardSourceTypeSchema = z.enum(HAZARD_SOURCE_TYPES);
export type HazardSourceType = z.infer<typeof HazardSourceTypeSchema>;

/**
 * A message's HAZARD BINDING (migration 0154) — the polymorphic (source_type, source_id) link,
 * mirroring anomaly_workstate. `source_id` is the hazard's canonical id (active_wildland_fires.id
 * for a fire). `source_ref` is a legible, human identifier (the fire NUMBER, e.g. 'K20524')
 * resolved server-side at read time — null when the hazard row has aged out (the durable link is
 * still `source_id`). The operator-facing "re: …" label is DERIVED client-side (communityComms
 * Vocabulary) so no doctrine string lives in the API.
 */
export const CommunityMessageHazardSchema = z.object({
  source_type: z.string(),
  source_id: z.string(),
  /** Legible human ref (the fire number) when the hazard still exists; null when aged out. */
  source_ref: z.string().nullable(),
});
export type CommunityMessageHazard = z.infer<typeof CommunityMessageHazardSchema>;

export const CommunityMessageSchema = z.object({
  id: z.string(),
  community_id: z.string(),
  /** The posting operator's resolved identity (server-stamped; never client-trusted). */
  author_operator_id: z.string(),
  /** Display label DERIVED from author_operator_id at serialization (the seat today). */
  author_label: z.string(),
  /** The posting operator's session — honestly null under AUTH-off. */
  author_session_id: z.string().nullable(),
  content: z.string(),
  message_type: z.string(),
  created_at: z.string(),
  /** Authored by the VIEWING operator — computed server-side, drives row alignment. */
  mine: z.boolean(),
  /** #453 — per-reader receipts on THIS message (every acknowledging operator EXCEPT the
   *  author). Present on every server response (COALESCEs to []); optional in the type so
   *  older fixtures/mocks omitting it still validate. */
  read_by: z.array(CommunityMessageReadReceiptSchema).optional(),
  /** #453 — did the VIEWING operator acknowledge this message? Server-computed (like `mine`).
   *  Always false on `mine` messages (a message is never self-acknowledged). */
  acknowledged_by_me: z.boolean().optional(),
  /** #453 — the viewing operator's OWN receipt time (ISO-8601 UTC), when acknowledged_by_me. */
  acknowledged_by_me_at: z.string().nullable().optional(),
  /** Migration 0154 — the hazard this message is bound to (a fire, when posted from the OC-fire
   *  panel), or null for an unbound coordination message. Present on every server response
   *  (server COALESCEs to null); optional in the type so older fixtures/mocks still validate. */
  hazard: CommunityMessageHazardSchema.nullable().optional(),
});
export type CommunityMessage = z.infer<typeof CommunityMessageSchema>;

/** Accepted community-message kinds (free-text column, API-validated). */
export const CommunityMessageTypeSchema = z.enum(['text']);

/** The POST body — content + an OPTIONAL hazard binding. The author is stamped server-side from
 *  the verified identity (never the body). The hazard binding, unlike identity, is legitimate
 *  client CONTEXT (which fire the operator is coordinating about — no forgery boundary: any
 *  operator may coordinate about any fire), so it rides the body. `hazard_source_type` is
 *  enum-validated; both fields are all-or-nothing (mirrors the DB CHECK — no half-linked row). */
export const CommunityMessageCreateSchema = z
  .object({
    content: z.string().trim().min(1).max(4000),
    message_type: CommunityMessageTypeSchema.optional().default('text'),
    /** Bind this message to a hazard (a fire, from the OC-fire panel). Omit for an unbound
     *  coordination message (the community / flood detail). `hazard_source_id` is a UUID — every
     *  hazard family in HAZARD_SOURCE_TYPES is uuid-keyed today (active_wildland_fires.id), so a
     *  non-uuid is rejected at the door (defense-in-depth over the read-time cast guard). A future
     *  non-uuid family would relax this alongside the enum. */
    hazard_source_type: HazardSourceTypeSchema.optional(),
    hazard_source_id: z.string().uuid().optional(),
  })
  .refine(
    (b) =>
      (b.hazard_source_type === undefined && b.hazard_source_id === undefined) ||
      (b.hazard_source_type !== undefined && b.hazard_source_id !== undefined),
    {
      message: 'hazard_source_type and hazard_source_id must be provided together (or neither)',
      path: ['hazard_source_id'],
    },
  );
export type CommunityMessageCreate = z.infer<typeof CommunityMessageCreateSchema>;

/** The acknowledge POST body (#453). The reader identity is stamped SERVER-SIDE from the
 *  verified seat (req.operator) — never the body; the optional `session_id` rides only into
 *  the audit trail (mirrors the peoc-arrivals acknowledge body). */
export const CommunityMessageAcknowledgeSchema = z.object({
  session_id: z.string().min(1).max(128).optional(),
});
export type CommunityMessageAcknowledge = z.infer<typeof CommunityMessageAcknowledgeSchema>;

/* ------------------------------------------------------------------ *
 * #454 — the PEOC's MULTI-community COORDINATION INBOX
 * ------------------------------------------------------------------ */

/**
 * #454 — one row of the PEOC's MULTI-community coordination inbox. A pure READ aggregate over
 * community_messages + community_message_reads (NO new table/column/verb): one row per community
 * with coordination traffic, so the PEOC can see WHICH communities have messages waiting on it
 * without opening each community on the map one at a time. Grounds the Ontario IMS §4.2 duty —
 * the PEOC "maintains a regular communication cycle with host and evacuated communities."
 *
 * `unread_count` = the VIEWING operator's OWN not-yet-acknowledged messages in the thread
 * (author_operator_id <> me AND no #453 receipt by me — the same acknowledge state the thread
 * uses, never a parallel last-viewed notion). It is the OPERATOR's own queue depth, NEVER the
 * community's behaviour / a watchlist (FN-dignity, standing). `last_activity_at` is the thread's
 * most-recent message time. A First-Nation row is a full peer of a municipal one — same shape,
 * no FN-only field.
 */
export const CommunityCoordinationInboxRowSchema = z.object({
  community_id: z.string(),
  name: z.string(),
  /** municipality | first_nation | unincorporated | metis_settlement | inuit_community | other */
  community_type: z.string(),
  /** ISO-8601 UTC time of the most recent message in this community's coordination thread. */
  last_activity_at: z.string(),
  /** The VIEWING operator's OWN not-yet-acknowledged messages in this thread (>= 0). */
  unread_count: z.number().int().nonnegative(),
  /** Migration 0154 — the hazard binding of the thread's MOST RECENT hazard-linked message (a
   *  fire), so the receiving seat sees WHICH fire is waiting on them without opening the thread.
   *  Null when no message in the thread carries a hazard link. */
  latest_hazard: CommunityMessageHazardSchema.nullable().optional(),
});
export type CommunityCoordinationInboxRow = z.infer<typeof CommunityCoordinationInboxRowSchema>;

/** GET /api/communities/comms-inbox — the PEOC's multi-community coordination inbox. */
export const CommunityCoordinationInboxResponseSchema = z.object({
  communities: z.array(CommunityCoordinationInboxRowSchema),
});
export type CommunityCoordinationInboxResponse = z.infer<
  typeof CommunityCoordinationInboxResponseSchema
>;
